import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { transactionsApi } from '../services/api/transactionsApi';
import { bankAccountsApi } from '../services/api';
import { Transaction, TransactionFilters, TransactionSummary, BankAccount } from '../types';
import { canCreate } from '../utils/permissions';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUrlState } from '../hooks/useUrlState';
import TransactionView from '../components/TransactionView';
import BankTransferModal from '../components/BankTransferModal';

type SortField = 'created_at' | 'amount' | 'type' | 'description';

const PER_PAGE_OPTIONS = [15, 25, 50, 100];

const TransactionsPage: React.FC = () => {
	usePageTitle('Transactions');
	const navigate = useNavigate();
	const currentUser = useSelector((state: RootState) => (state as any).auth.user);
	const [transactions, setTransactions] = useState<Transaction[]>([]);
	const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
	const [summary, setSummary] = useState<TransactionSummary | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [totalPages, setTotalPages] = useState(1);
	const [totalItems, setTotalItems] = useState(0);
	const [showBankTransferModal, setShowBankTransferModal] = useState(false);
	const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
	const [showTransactionView, setShowTransactionView] = useState(false);

	// URL state management
	const [searchParams] = useSearchParams();
	const {
		perPage,
		currentPage,
		sortField,
		sortDirection,
		searchTerm,
		startDate: dateFrom,
		endDate: dateTo,
		typeFilter,
		setPerPage,
		setCurrentPage,
		setSortField,
		setSortDirection,
		setSearchTerm,
		setStartDate: setDateFrom,
		setEndDate: setDateTo,
		setTypeFilter,
		clearFilters,
		updateUrl
	} = useUrlState({
		defaultPerPage: 15,
		defaultSortField: 'created_at',
		defaultSortDirection: 'desc'
	});

	// Extra filter state from URL
	const selectedBankAccount = searchParams.get('bankAccount') || 'all';
	const amountMin = searchParams.get('amountMin') || '';
	const amountMax = searchParams.get('amountMax') || '';
	const activeTab = ((typeFilter || 'all') as 'all' | 'income' | 'expense' | 'transfer');

	const setSelectedBankAccount = (val: string) => updateUrl({ bankAccount: val === 'all' ? '' : val, page: 1 });
	const setAmountMin = (val: string) => updateUrl({ amountMin: val, page: 1 });
	const setAmountMax = (val: string) => updateUrl({ amountMax: val, page: 1 });

	useEffect(() => {
		fetchBankAccounts();
	}, []);

	useEffect(() => {
		fetchTransactions(currentPage);
		fetchSummary();
		// eslint-disable-next-line
	}, [currentPage, sortField, sortDirection, perPage, searchTerm, activeTab, selectedBankAccount, dateFrom, dateTo, amountMin, amountMax]);

	const fetchBankAccounts = async () => {
		try {
			const response = await bankAccountsApi.getAll({ per_page: 1000 });
			setBankAccounts(response.data.data || []);
		} catch (err: any) {
			console.error('Failed to fetch bank accounts:', err);
		}
	};

	const fetchTransactions = async (page: number) => {
		setLoading(true);
		setError(null);
		try {
			const perPageNum = typeof perPage === 'number' ? perPage : parseInt(perPage);
			const filters: TransactionFilters = {
				type: activeTab,
				bank_account_id: selectedBankAccount === 'all' ? undefined : parseInt(selectedBankAccount),
				date_from: dateFrom || undefined,
				date_to: dateTo || undefined,
				amount_min: amountMin ? parseFloat(amountMin) : undefined,
				amount_max: amountMax ? parseFloat(amountMax) : undefined,
				search: searchTerm || undefined,
				sort_by: sortField as SortField,
				sort_order: sortDirection,
				per_page: perPageNum,
				page: page
			};

			const response = await transactionsApi.getAll(filters);
			setTransactions(response.data.data.data || []);
			setTotalPages(response.data.data.last_page || 1);
			setTotalItems(response.data.data.total || 0);
		} catch (err: any) {
			setError(err.response?.data?.message || 'Failed to fetch transactions');
		} finally {
			setLoading(false);
		}
	};

	const fetchSummary = async () => {
		try {
			const filters = {
				date_from: dateFrom || undefined,
				date_to: dateTo || undefined
			};
			const response = await transactionsApi.getSummary(filters);
			setSummary(response.data.data);
		} catch (err: any) {
			console.error('Failed to fetch summary:', err);
		}
	};

	const handleTabChange = (tab: 'all' | 'income' | 'expense' | 'transfer') => {
		setTypeFilter(tab === 'all' ? '' : tab);
		setCurrentPage(1);
	};

	const handleViewTransaction = (transaction: Transaction) => {
		setSelectedTransaction(transaction);
		setShowTransactionView(true);
	};

	const handleEditTransaction = (transaction: Transaction) => {
		navigate(`/accounting/transactions/${transaction.id}/edit`);
	};

	const handleDeleteTransaction = async (transaction: Transaction) => {
		if (!window.confirm('Are you sure you want to delete this transaction?')) {
			return;
		}

		setLoading(true);
		try {
			await transactionsApi.delete(transaction.id!);
			await fetchTransactions(currentPage);
			await fetchSummary();
		} catch (err: any) {
			setError(err.response?.data?.message || 'Failed to delete transaction');
		} finally {
			setLoading(false);
		}
	};

	const handlePageChange = (page: number) => {
		if (page >= 1 && page <= totalPages) {
			setCurrentPage(page);
		}
	};

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
	};

	const handlePerPageChange = (newPerPage: number) => {
		setPerPage(newPerPage);
	};

	const handleSearchChange = (term: string) => {
		setSearchTerm(term);
	};

	const clearAllFilters = () => {
		clearFilters();
	};

	const formatAmount = (amount: number | string) => {
		const num = typeof amount === 'string' ? parseFloat(amount) : amount;
		return new Intl.NumberFormat('en-CA', {
			style: 'currency',
			currency: 'CAD'
		}).format(num);
	};

	const getTransactionTypeColor = (type: string) => {
		switch (type) {
			case 'income':
				return 'text-green-600 bg-green-100';
			case 'expense':
				return 'text-red-600 bg-red-100';
			case 'transfer':
				return 'text-blue-600 bg-blue-100';
			default:
				return 'text-gray-600 bg-gray-100';
		}
	};

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			<div className="space-y-6">
				<div className="flex justify-between items-center">
					<h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
					<div className="flex space-x-3">
						{canCreate(currentUser) && (
							<>
								<button
									onClick={() => setShowBankTransferModal(true)}
									className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
								>
									Bank Transfer
								</button>
								<button
									onClick={() => navigate('/accounting/transactions/add')}
									className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
								>
									Add Transaction
								</button>
							</>
						)}
					</div>
				</div>

				{/* Summary Cards */}
				{summary && (
					<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
						<div className="bg-white p-4 rounded-lg border border-gray-200">
							<div className="text-sm font-medium text-gray-600">Total Income</div>
							<div className="text-lg font-bold text-green-600">
								{formatAmount(summary.total_income)}
							</div>
						</div>
						<div className="bg-white p-4 rounded-lg border border-gray-200">
							<div className="text-sm font-medium text-gray-600">Total Expenses</div>
							<div className="text-lg font-bold text-red-600">
								{formatAmount(summary.total_expenses)}
							</div>
						</div>
						<div className="bg-white p-4 rounded-lg border border-gray-200">
							<div className="text-sm font-medium text-gray-600">Total Transfers</div>
							<div className="text-lg font-bold text-blue-600">
								{formatAmount(summary.total_transfers)}
							</div>
						</div>
						<div className="bg-white p-4 rounded-lg border border-gray-200">
							<div className="text-sm font-medium text-gray-600">Net Amount</div>
							<div className={`text-lg font-bold ${parseFloat(summary.net_amount.toString()) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
								{formatAmount(summary.net_amount)}
							</div>
						</div>
					</div>
				)}

				{/* Tab Navigation */}
				<div className="border-b border-gray-200">
					<nav className="-mb-px flex space-x-8">
						{[
							{ key: 'all', label: 'All Transactions' },
							{ key: 'income', label: 'Income' },
							{ key: 'expense', label: 'Expenses' },
							{ key: 'transfer', label: 'Transfers' }
						].map((tab) => (
							<button
								key={tab.key}
								onClick={() => handleTabChange(tab.key as any)}
								className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.key
										? 'border-blue-500 text-blue-600'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
									}`}
							>
								{tab.label}
							</button>
						))}
					</nav>
				</div>

				{/* Filters Section */}
				<div className="bg-white p-4 rounded-lg border border-gray-200">
					<div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
						<div>
							<label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
								Search
							</label>
							<input
								type="text"
								id="search"
								value={searchTerm}
								onChange={(e) => handleSearchChange(e.target.value)}
								placeholder="Search transactions..."
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
							/>
						</div>

						<div>
							<label htmlFor="bankAccount" className="block text-sm font-medium text-gray-700 mb-1">
								Bank Account
							</label>
							<select
								id="bankAccount"
								value={selectedBankAccount}
								onChange={(e) => setSelectedBankAccount(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
							>
								<option value="all">All Accounts</option>
								{bankAccounts.map((account) => (
									<option key={account.id} value={account.id!.toString()}>
										{account.account_name} ({account.masked_account_number})
									</option>
								))}
							</select>
						</div>

						<div>
							<label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700 mb-1">
								Date From
							</label>
							<input
								type="date"
								id="dateFrom"
								value={dateFrom}
								onChange={(e) => setDateFrom(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
							/>
						</div>

						<div>
							<label htmlFor="dateTo" className="block text-sm font-medium text-gray-700 mb-1">
								Date To
							</label>
							<input
								type="date"
								id="dateTo"
								value={dateTo}
								onChange={(e) => setDateTo(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
							/>
						</div>

						<div>
							<label htmlFor="amountMin" className="block text-sm font-medium text-gray-700 mb-1">
								Min Amount
							</label>
							<input
								type="number"
								id="amountMin"
								value={amountMin}
								onChange={(e) => setAmountMin(e.target.value)}
								placeholder="0.00"
								step="0.01"
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
							/>
						</div>

						<div>
							<label htmlFor="amountMax" className="block text-sm font-medium text-gray-700 mb-1">
								Max Amount
							</label>
							<input
								type="number"
								id="amountMax"
								value={amountMax}
								onChange={(e) => setAmountMax(e.target.value)}
								placeholder="0.00"
								step="0.01"
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
							/>
						</div>
					</div>

					<div className="flex justify-between items-center mt-4">
						<button
							onClick={clearAllFilters}
							className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
						>
							Clear All Filters
						</button>

						<div className="flex items-center space-x-4">
							<label htmlFor="perPage" className="text-sm font-medium text-gray-700">
								Per Page:
							</label>
							<select
								id="perPage"
								value={perPage}
								onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
								className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
							>
								{PER_PAGE_OPTIONS.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>

				{/* Error Alert */}
				{error && (
					<div className="bg-red-50 border border-red-200 rounded-md p-4">
						<div className="text-red-800">{error}</div>
					</div>
				)}

				{/* Transactions Table */}
				<div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
										onClick={() => handleSort('created_at')}
									>
										Date
										{sortField === 'created_at' && (
											<span className="ml-1">
												{sortDirection === 'asc' ? '↑' : '↓'}
											</span>
										)}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
										onClick={() => handleSort('type')}
									>
										Type
										{sortField === 'type' && (
											<span className="ml-1">
												{sortDirection === 'asc' ? '↑' : '↓'}
											</span>
										)}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
										onClick={() => handleSort('description')}
									>
										Description
										{sortField === 'description' && (
											<span className="ml-1">
												{sortDirection === 'asc' ? '↑' : '↓'}
											</span>
										)}
									</th>
									<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Bank Account
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
										onClick={() => handleSort('amount')}
									>
										Amount
										{sortField === 'amount' && (
											<span className="ml-1">
												{sortDirection === 'asc' ? '↑' : '↓'}
											</span>
										)}
									</th>
									<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Reference
									</th>
									<th scope="col" className="relative px-6 py-3">
										<span className="sr-only">Actions</span>
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{loading ? (
									<tr>
										<td colSpan={7} className="px-6 py-4 text-center text-gray-500">
											Loading...
										</td>
									</tr>
								) : transactions.length === 0 ? (
									<tr>
										<td colSpan={7} className="px-6 py-4 text-center text-gray-500">
											No transactions found
										</td>
									</tr>
								) : (
									transactions.map((transaction) => (
										<tr key={transaction.id} className="hover:bg-gray-50">
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
												{transaction.formatted_created_at || new Date(transaction.created_at!).toLocaleDateString()}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTransactionTypeColor(transaction.type)}`}>
													{transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
												</span>
											</td>
											<td className="px-6 py-4 text-sm text-gray-900">
												{transaction.description}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
												{transaction.bank_account?.account_name || 'N/A'}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
												<span className={transaction.type === 'expense' ? 'text-red-600' : 'text-green-600'}>
													{transaction.type === 'expense' ? '-' : '+'}{formatAmount(transaction.amount)}
												</span>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{transaction.reference_number || '-'}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
												<div className="flex space-x-2">
													<button
														onClick={() => handleViewTransaction(transaction)}
														className="text-blue-600 hover:text-blue-900"
													>
														View
													</button>
													{transaction.canBeUpdatedBy?.(currentUser) && (
														<button
															onClick={() => handleEditTransaction(transaction)}
															className="text-yellow-600 hover:text-yellow-900"
														>
															Edit
														</button>
													)}
													{transaction.canBeDeletedBy?.(currentUser) && (
														<button
															onClick={() => handleDeleteTransaction(transaction)}
															className="text-red-600 hover:text-red-900"
														>
															Delete
														</button>
													)}
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
							<div className="flex-1 flex justify-between sm:hidden">
								<button
									onClick={() => handlePageChange(currentPage - 1)}
									disabled={currentPage === 1}
									className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									Previous
								</button>
								<button
									onClick={() => handlePageChange(currentPage + 1)}
									disabled={currentPage === totalPages}
									className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									Next
								</button>
							</div>
							<div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
								<div>
									<p className="text-sm text-gray-700">
										Showing{' '}
										<span className="font-medium">{(currentPage - 1) * (typeof perPage === 'number' ? perPage : parseInt(perPage)) + 1}</span>
										{' '}to{' '}
										<span className="font-medium">
											{Math.min(currentPage * (typeof perPage === 'number' ? perPage : parseInt(perPage)), totalItems)}
										</span>
										{' '}of{' '}
										<span className="font-medium">{totalItems}</span>
										{' '}results
									</p>
								</div>
								<div>
									<nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
										<button
											onClick={() => handlePageChange(currentPage - 1)}
											disabled={currentPage === 1}
											className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
										>
											Previous
										</button>
										{Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
											const page = i + 1;
											return (
												<button
													key={page}
													onClick={() => handlePageChange(page)}
													className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page
															? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
															: 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
														}`}
												>
													{page}
												</button>
											);
										})}
										<button
											onClick={() => handlePageChange(currentPage + 1)}
											disabled={currentPage === totalPages}
											className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
										>
											Next
										</button>
									</nav>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Bank Transfer Modal */}
			<BankTransferModal
				isOpen={showBankTransferModal}
				onClose={() => setShowBankTransferModal(false)}
				onSuccess={() => {
					fetchTransactions(currentPage);
					fetchSummary();
				}}
			/>

			{/* Transaction View Modal */}
			{showTransactionView && selectedTransaction && (
				<TransactionView
					transaction={selectedTransaction}
					onClose={() => {
						setShowTransactionView(false);
						setSelectedTransaction(null);
					}}
				/>
			)}
		</div>
	);
};

export default TransactionsPage;
